import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { api, setToken, ApiError } from "@/lib/api";
import { APP_NAME, APP_TAGLINE, LOGO_SRC } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BlueprintBackdrop } from "@/components/blueprint-backdrop";
import { Eye, EyeOff } from "lucide-react";

interface TokenStatus {
    valid: boolean;
    purpose?: "invite" | "password_reset";
    email?: string;
    firstName?: string;
}

export default function SetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token") ?? "";

    const [status, setStatus] = useState<TokenStatus | null>(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        if (!token) {
            setStatus({ valid: false });
            return;
        }
        api.get<TokenStatus>(`/api/auth/token-status?token=${encodeURIComponent(token)}`)
            .then((s) => {
                if (active) setStatus(s);
            })
            .catch(() => {
                if (active) setStatus({ valid: false });
            });
        return () => {
            active = false;
        };
    }, [token]);

    const isInvite = status?.purpose === "invite";
    const heading = isInvite ? `Welcome to ${APP_NAME}` : "Reset your password";
    const cta = isInvite ? "Set password & sign in" : "Update password & sign in";

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setSubmitting(true);
        try {
            const data = await api.post<{ token: string }>("/api/auth/set-password", { token, password });
            setToken(data.token);
            // Full navigation so AuthProvider re-initializes from the new token.
            window.location.assign("/");
        } catch (err) {
            const code = err instanceof ApiError ? (err.body as { error?: string })?.error : undefined;
            setError(
                code === "INVALID_OR_EXPIRED_TOKEN"
                    ? "This link is invalid or has expired. Ask an admin to resend it."
                    : "Something went wrong. Please try again.",
            );
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <BlueprintBackdrop verticalAnchor="viewport" />
            <div className="relative w-full max-w-sm">
                <Card className="shadow-[var(--shadow-elevated)] border-border/40 bg-card">
                    <CardHeader className="text-center pb-3 pt-8">
                        <h1 className="sr-only">{heading}</h1>
                        <div className="mx-auto mb-5 flex justify-center">
                            <img
                                src={LOGO_SRC}
                                alt=""
                                className="h-[7.5rem] w-auto max-w-full object-contain object-center"
                                width={320}
                                height={120}
                            />
                        </div>
                        <p className="text-sm font-medium text-foreground">{heading}</p>
                        {status?.valid && status.email && (
                            <p className="text-sm text-foreground/80 mt-1">{status.email}</p>
                        )}
                    </CardHeader>
                    <CardContent>
                        {status === null ? (
                            <p className="text-sm text-foreground/80 text-center py-4">Checking your link…</p>
                        ) : !status.valid ? (
                            <div className="space-y-4">
                                <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3 border border-destructive/20">
                                    This link is invalid or has expired. Ask an administrator to resend your invite or
                                    reset link.
                                </div>
                                <Button asChild variant="outline" className="w-full">
                                    <a href="/login">Back to sign in</a>
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3 border border-destructive/20">
                                        {error}
                                    </div>
                                )}
                                <div className="space-y-1.5">
                                    <label htmlFor="password" className="text-sm font-medium">
                                        New password
                                    </label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={8}
                                            autoFocus
                                            className="h-10 pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/80 hover:text-foreground transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label htmlFor="confirm" className="text-sm font-medium">
                                        Confirm password
                                    </label>
                                    <Input
                                        id="confirm"
                                        type={showPassword ? "text" : "password"}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        minLength={8}
                                        className="h-10"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full h-10 font-semibold shadow-md bg-primary hover:bg-primary/90 text-primary-foreground border-0"
                                    disabled={submitting}
                                >
                                    {submitting ? "Saving…" : cta}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
                <p className="text-center text-xs text-shell-foreground/80 mt-6 max-w-xs mx-auto">{APP_TAGLINE}</p>
            </div>
        </div>
    );
}
